import {
  CountByDate,
  GenderCountGroup,
} from "interface/admin/interface.dashboard";
import { format } from "date-fns";
import { ko } from "date-fns/locale";
import { PeriodOption } from "components/admin/dashboard/dashboard.select";

// userTotalCount, activeUserCount에서 인원만 뺴기 함수
export const getTotalUserCount = (groups: GenderCountGroup[]): number => {
  return groups?.reduce((sum, group) => {
    return sum + (group.maleCount || 0) + (group.femaleCount || 0);
  }, 0);
};

// 검사, 측정 count만 빼서 더하기 함수
export const getTotalCountFromDateGroups = (items: CountByDate[]): number => {
  return items?.reduce((sum, item) => sum + (item.count || 0), 0);
};

export const formatDateOnly = (date: Date) => {
  const yyyy = date.getFullYear();
  const MM = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${MM}-${dd}`; // 예: 2025-04-08
};

export const formatWithDay = (date: Date | null) => {
  if (!date) return "";
  return format(date, "yyyy.MM.dd (eee)", { locale: ko });
};

export const getWeekRangeFromToday = (): [Date, Date] => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(today);
  const day = today.getDay(); // 0: 일요일
  start.setDate(today.getDate() - day); // 주 시작: 일요일

  const end = new Date(start);
  end.setDate(start.getDate() + 6);

  // 오늘이 포함된 주라면 끝을 오늘로 제한
  if (today >= start && today <= end) {
    end.setTime(today.getTime());
  }

  return [start, end];
};

export const isTodayInRange = (
  start: Date | null,
  end: Date | null
): boolean => {
  if (!start || !end) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0); // 오늘 기준 정리

  return start <= today && end >= today;
};

const formatDateToLocalISOString = (date: Date): string => {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} 00:00:00`;
};

export const getPreviousRange = (
  start: string,
  end: string,
  period: PeriodOption
): { prevStart: string; prevEnd: string } => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  let prevStart: Date;
  let prevEnd: Date;

  switch (period) {
    case "daily":
      prevEnd = new Date(startDate);
      prevEnd.setDate(prevEnd.getDate() - 1);

      prevStart = new Date(prevEnd);
      prevStart.setDate(prevEnd.getDate() - 6);
      break;

    case "weekly":
      // 전주의 '일요일 ~ 토요일'
      const currentWeekStart = new Date(startDate);
      const currentWeekEnd = new Date(startDate);
      currentWeekStart.setDate(
        currentWeekStart.getDate() - currentWeekStart.getDay()
      ); // 일요일
      currentWeekEnd.setDate(currentWeekStart.getDate() + 6); // 토요일

      prevStart = new Date(currentWeekStart);
      prevStart.setDate(prevStart.getDate() - 7);

      prevEnd = new Date(currentWeekEnd);
      prevEnd.setDate(prevEnd.getDate() - 7);
      break;

    case "monthly":
      prevStart = new Date(startDate);
      prevStart.setMonth(prevStart.getMonth() - 1);
      prevStart.setDate(1);

      prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0);
      break;
  }

  return {
    prevStart: formatDateToLocalISOString(prevStart),
    prevEnd: formatDateToLocalISOString(prevEnd),
  };
};

interface ChangeRateResult {
  label: string;
  isIncrease: boolean;
  isEqual: boolean;
}

export const getChangeRateLabel = (
  current: number,
  previous: number
): ChangeRateResult => {
  if (previous === 0) {
    if (current === 0) return { label: "0%", isIncrease: false, isEqual: true };
    return { label: `${current}명 증가`, isIncrease: true, isEqual: false };
  }

  const diff = current - previous;
  const rate = (diff / previous) * 100;
  const rounded = Math.abs(rate).toFixed(1); // 부호 제거됨

  const isEqual = diff === 0;
  const isIncrease = diff > 0;

  return {
    label: isEqual ? "0%" : `${rounded}%`,
    isIncrease,
    isEqual,
  };
};
